import { Collapsible } from "@base-ui/react/collapsible";
import { cva } from "class-variance-authority";
import { useState } from "react";
import {
  ContentContainerBody,
  ContentContainerFooter,
  ContentContainerRoot,
  ContentContainerTitle,
} from "../content-container/content-container-internals";
import { Icon } from "../icon";

const accordionRootStyles = cva([
  "hover:bg-surface-container-low",
  "cursor-pointer",
  "transition-colors duration-200 ease-in-out",
  "has-[:focus-visible]:[outline-width:var(--spacing-2)]",
  "has-[:focus-visible]:outline-solid",
  "has-[:focus-visible]:outline-tertiary-tertiary",
  "has-[:focus-visible]:[outline-offset:3px]",
  "data-[disabled]:opacity-50",
  "data-[disabled]:cursor-default",
]);

const accordionTriggerStyles = cva([
  "w-full",
  "flex items-center gap-(--spacing-8)",
  "cursor-pointer",
  "min-h-(--spacing-24)",
  "text-left",
  "focus-visible:outline-none",
  "disabled:pointer-events-none",
  "disabled:cursor-default",
]);

const chevronTriggerStyles = cva([
  "group",
  "flex size-(--spacing-32) shrink-0 items-center justify-center",
  "rounded-(--radius-sm)",
  "cursor-pointer",
  "focus-visible:outline-none",
  "disabled:pointer-events-none",
  "disabled:cursor-default",
]);

const accordionPanelStyles = cva([
  "w-full",
  "min-w-0",
  "overflow-hidden",
  "h-(--collapsible-panel-height)",
  "[&[data-starting-style]]:h-0",
  "[&[data-ending-style]]:h-0",
  "mt-(--spacing-12)",
  "[&[data-starting-style]]:mt-0",
  "[&[data-ending-style]]:mt-0",
  "transition-[height,margin-top] duration-150 ease-in-out",
  "motion-reduce:transition-none",
]);

const accordionShellClickIgnoreSelector = [
  "[data-accordion-panel-content]",
  "[data-accordion-ignore-shell-click]",
  "button",
  "a",
  "input",
  "label",
  "select",
  "textarea",
  "[role='button']",
  "[role='link']",
  "[role='menuitem']",
  "[role='option']",
  "[role='switch']",
  "[role='checkbox']",
  "[role='radio']",
].join(",");

function isAccordionShellClickTarget(target: EventTarget) {
  return target instanceof Element
    ? target.closest(accordionShellClickIgnoreSelector) === null
    : false;
}

export type AccordionProps = Omit<
  Collapsible.Root.Props,
  "className" | "onOpenChange" | "children" | "title"
> & {
  /** Called when the open state changes. */
  onOpenChange?: (open: boolean) => void;
  /** Heading content rendered in the trigger. */
  title: React.ReactNode;
  /** Content shown at the start of the header. */
  startAccessory?: React.ReactNode;
  /** Content shown beside the title. */
  titleAccessory?: React.ReactNode;
  /** Content shown at the end of the header. */
  actions?: React.ReactNode;
  /** Content shown below the header. */
  summary?: React.ReactNode;
  /** Content shown in the open panel. */
  children: React.ReactNode;
  /** Content shown below the panel body. */
  footer?: React.ReactNode;
};

function AccordionComponent({
  open,
  defaultOpen,
  onOpenChange,
  title,
  startAccessory,
  titleAccessory,
  actions,
  summary,
  children,
  footer,
  disabled,
  ...rootProps
}: AccordionProps) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(
    () => defaultOpen ?? false,
  );
  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : uncontrolledOpen;

  function setOpen(nextOpen: boolean) {
    if (!isControlled) {
      setUncontrolledOpen(nextOpen);
    }

    onOpenChange?.(nextOpen);
  }

  function handleShellClick(event: React.MouseEvent<HTMLDivElement>) {
    if (disabled || !isAccordionShellClickTarget(event.target)) {
      return;
    }

    setOpen(!isOpen);
  }

  return (
    <Collapsible.Root
      {...rootProps}
      open={isOpen}
      onOpenChange={setOpen}
      disabled={disabled}
      className="group"
    >
      <ContentContainerRoot
        padding="standard"
        data-disabled={disabled ? "" : undefined}
        className={accordionRootStyles()}
        onClick={handleShellClick}
      >
        <div className="flex flex-col gap-(--spacing-8) w-full">
          <div className="flex items-center gap-(--spacing-8) w-full">
            {startAccessory && (
              <div className="flex shrink-0 items-center">{startAccessory}</div>
            )}

            <div className="flex flex-1 min-w-0 items-center gap-(--spacing-8)">
              <h3 className="flex min-w-0 flex-1 m-0">
                <Collapsible.Trigger
                  className={accordionTriggerStyles()}
                  onClick={(event) => event.stopPropagation()}
                >
                  <ContentContainerTitle className="flex-1">
                    {title}
                  </ContentContainerTitle>
                </Collapsible.Trigger>
              </h3>
              {titleAccessory && (
                <div className="flex shrink-0 items-center">
                  {titleAccessory}
                </div>
              )}
            </div>

            {actions && (
              <div
                data-accordion-ignore-shell-click=""
                className="flex shrink-0 items-center"
              >
                {actions}
              </div>
            )}

            <Collapsible.Trigger
              aria-label="Toggle accordion"
              data-slot="accordion-chevron-trigger"
              className={chevronTriggerStyles()}
              onClick={(event) => event.stopPropagation()}
            >
              <span className="flex group-data-[panel-open]:hidden">
                <Icon name="chevron-down" size="md" tone="secondary" />
              </span>
              <span className="hidden group-data-[panel-open]:flex">
                <Icon name="chevron-up" size="md" tone="secondary" />
              </span>
            </Collapsible.Trigger>
          </div>

          {summary && <div className="w-full min-w-0">{summary}</div>}
        </div>

        <Collapsible.Panel className={accordionPanelStyles()}>
          <div
            data-accordion-panel-content=""
            className="flex flex-col gap-(--spacing-4) cursor-default"
          >
            <ContentContainerBody>{children}</ContentContainerBody>
            {footer && (
              <ContentContainerFooter>{footer}</ContentContainerFooter>
            )}
          </div>
        </Collapsible.Panel>
      </ContentContainerRoot>
    </Collapsible.Root>
  );
}

export { AccordionComponent as Accordion };
